import {
  LAST_STEP_DONE,
  VALUELESS_OPS,
  WORKFLOW_OPS,
  type WorkflowCondition,
  type WorkflowFieldDef,
  type WorkflowOp,
  type WorkflowSubject,
} from "@dentacore/shared-types";

import { conditionFields, conditionFor, defaultCondition, findField } from "./graph-model";
import type { WorkflowRefs } from "./use-workflow-refs";
import styles from "./workflows.module.css";

/**
 * **A condition: a field, a rule and a value** (WG-D6). The field list is the
 * record's own fields and those of the records it links to; the rules are the
 * ones that field's type offers; the value box fits the field — a list for a
 * status, a number for a count, an amount for money (typed in whole units,
 * kept in minor units, CLAUDE.md), a number of days for "within the next …".
 */
export function ConditionFields({
  subject,
  value,
  onChange,
  refs,
  disabled = false,
  testPrefix = "wf-cond",
}: {
  subject: WorkflowSubject;
  value: WorkflowCondition;
  onChange: (next: WorkflowCondition) => void;
  refs: WorkflowRefs;
  disabled?: boolean;
  testPrefix?: string;
}): React.JSX.Element {
  const fields = conditionFields(subject);
  const def = findField(subject, value.field);
  const ops = def === null ? [] : WORKFLOW_OPS[def.type];

  return (
    <div className={styles.condFields}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Field</span>
        <select
          className={styles.input}
          value={def === null ? "" : value.field}
          disabled={disabled}
          onChange={(event) => {
            const picked = fields.find((f) => f.key === event.target.value);
            if (picked !== undefined) onChange(conditionFor(picked.key, picked.def));
          }}
          data-testid={`${testPrefix}-field`}
        >
          {def === null && <option value="">Pick a field</option>}
          {fields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
      </label>
      {def !== null && (
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Rule</span>
          <select
            className={styles.input}
            value={value.op}
            disabled={disabled}
            onChange={(event) => {
              const op = event.target.value as WorkflowOp;
              onChange(
                VALUELESS_OPS.includes(op)
                  ? { field: value.field, op }
                  : { field: value.field, op, value: keepValue(def, op, value.value) },
              );
            }}
            data-testid={`${testPrefix}-op`}
          >
            {ops.map((o) => (
              <option key={o.op} value={o.op}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {def !== null && !VALUELESS_OPS.includes(value.op) && (
        <ValueBox
          def={def}
          op={value.op}
          value={value.value}
          refs={refs}
          disabled={disabled}
          onChange={(next) => onChange({ ...value, value: next })}
          testId={`${testPrefix}-value`}
        />
      )}
    </div>
  );
}

/** A value carried across a change of rule only when it still fits. */
function keepValue(
  def: WorkflowFieldDef,
  op: WorkflowOp,
  value: WorkflowCondition["value"],
): WorkflowCondition["value"] {
  const numeric =
    def.type === "number" || def.type === "money" || op === "withinDays" || op === "olderThanDays";
  if (numeric) return typeof value === "number" ? value : 0;
  if (def.type === "enum")
    return typeof value === "string" ? value : (def.options?.[0]?.value ?? "");
  return typeof value === "string" ? value : "";
}

function ValueBox({
  def,
  op,
  value,
  refs,
  disabled,
  onChange,
  testId,
}: {
  def: WorkflowFieldDef;
  op: WorkflowOp;
  value: WorkflowCondition["value"];
  refs: WorkflowRefs;
  disabled: boolean;
  onChange: (next: string | number) => void;
  testId: string;
}): React.JSX.Element {
  if (op === "withinDays" || op === "olderThanDays")
    return (
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Days</span>
        <input
          className={styles.input}
          type="number"
          min={0}
          step={1}
          value={typeof value === "number" ? value : ""}
          disabled={disabled}
          onChange={(event) => onChange(Math.max(0, Math.round(Number(event.target.value) || 0)))}
          data-testid={testId}
        />
      </label>
    );
  if (def.type === "enum")
    return (
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Value</span>
        <select
          className={styles.input}
          value={typeof value === "string" ? value : ""}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          data-testid={testId}
        >
          {def.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  if (def.type === "money")
    return (
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Amount ({refs.currency})</span>
        <input
          className={styles.input}
          type="number"
          min={0}
          step="any"
          value={typeof value === "number" ? refs.fromMinor(value) : ""}
          disabled={disabled}
          onChange={(event) => onChange(refs.toMinor(Number(event.target.value) || 0))}
          data-testid={testId}
        />
      </label>
    );
  if (def.type === "number")
    return (
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Value</span>
        <input
          className={styles.input}
          type="number"
          step="any"
          value={typeof value === "number" ? value : ""}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value) || 0)}
          data-testid={testId}
        />
      </label>
    );
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>Value</span>
      <input
        className={styles.input}
        type="text"
        value={typeof value === "string" ? value : ""}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      />
    </label>
  );
}

/**
 * **An arrow's condition** (WG-D4, WG-D6): *Always*, *Only if the last step was
 * done* — never on the first arrow — or a field condition. `null` is *Always*.
 */
export function ArrowCondition({
  subject,
  value,
  first,
  onChange,
  refs,
}: {
  subject: WorkflowSubject;
  value: WorkflowCondition | null;
  /** Out of the Start box: *last step was done* is not offered. */
  first: boolean;
  onChange: (next: WorkflowCondition | null) => void;
  refs: WorkflowRefs;
}): React.JSX.Element {
  const mode = value === null ? "always" : value.field === LAST_STEP_DONE ? "lastDone" : "field";
  return (
    <div className={styles.stack}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Carry on</span>
        <select
          className={styles.input}
          value={mode}
          onChange={(event) => {
            const next = event.target.value;
            onChange(
              next === "always"
                ? null
                : next === "lastDone"
                  ? { field: LAST_STEP_DONE, op: "yes" }
                  : defaultCondition(subject),
            );
          }}
          data-testid="wf-edge-mode"
        >
          <option value="always">Always</option>
          {!first && <option value="lastDone">Only if the last step was done</option>}
          <option value="field">Only if…</option>
        </select>
      </label>
      {value !== null && mode === "field" && (
        <ConditionFields subject={subject} value={value} onChange={onChange} refs={refs} />
      )}
      <p className={styles.hint}>
        When this is false the run stops here and the log says why.
        {first ? " The first arrow cannot wait for a step — there is none before it." : ""}
      </p>
    </div>
  );
}
